import { Rule } from "antd/es/form";

export const stringRules: Rule[] = [
  {
    pattern: /\w+/gi,
    message: "Please enter a valid value.",
  },
  {
    required: true,
    message: "This field is required.",
  },
];

// Mirror of server/shared/email/validateEmailAddress.ts's CORRECTION_MAP
// (the canonical typo map — server/services/emailTypoCheck.ts imports it
// rather than carrying its own copy) — keep in sync when adding entries.
// Driven by real lead-welcome bounces in production. Pinned against the
// server map by server/shared/email/clientTypoMapParity.test.ts — a
// mismatch here fails that suite with the exact diverged entry.
const EMAIL_TYPO_CORRECTIONS: Record<string, string> = {
  "gmail.con": "gmail.com",
  "gmail.co": "gmail.com",
  "gmal.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gtmel.com": "gmail.com",
  "gmali.com": "gmail.com",
  "gemail.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmsil.com": "gmail.com",
  "gmail.vom": "gmail.com",
  "gmail.cpm": "gmail.com",
  "glail.com": "gmail.com",
  "yahoo.con": "yahoo.com",
  "yhaoo.com": "yahoo.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoi.com": "yahoo.com",
  "yaoo.com": "yahoo.com",
  "yahoo.vom": "yahoo.com",
  "hotmal.com": "hotmail.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotnail.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "outloook.com": "outlook.com",
  "outlook.con": "outlook.com",
  "aol.con": "aol.com",
  "aoll.com": "aol.com",
  "icloud.con": "icloud.com",
  "iclould.com": "icloud.com",
  "iclod.com": "icloud.com",
  "icoud.com": "icloud.com",
  "ivloud.com": "icloud.com",
  "icloud.cl": "icloud.com",
};

const NON_DELIVERABLE_DOMAINS = new Set([
  "email.com",
  "test.com",
  "example.com",
  "mail.com",
]);

export function suggestEmailCorrection(email: string): string | null {
  if (!email) return null;
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  const domain = email.slice(at + 1).toLowerCase();
  const mapped = EMAIL_TYPO_CORRECTIONS[domain];
  if (mapped) return email.slice(0, at + 1) + mapped;
  if (/\.con$/.test(domain)) {
    return email.slice(0, at + 1) + domain.replace(/\.con$/, ".com");
  }
  return null;
}

export function isNonDeliverableEmail(email: string): boolean {
  if (!email) return false;
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  return NON_DELIVERABLE_DOMAINS.has(email.slice(at + 1).toLowerCase());
}

export const emailRules: Rule[] = [
  {
    type: "email",
    message: "Please enter a valid email.",
  },
  {
    required: true,
    message: "Your email is required.",
  },
  {
    validator: (_, value: string) => {
      if (!value) return Promise.resolve();
      if (isNonDeliverableEmail(value)) {
        return Promise.reject(
          new Error("That email domain isn't accepted. Please use a real address."),
        );
      }
      return Promise.resolve();
    },
  },
];

export const zipRules: Rule[] = [
  {
    pattern: /\d+/gi,
    message: "Please enter a valid zipcode.",
  },
  {
    required: true,
    message: "Your zipcode is required.",
  },
];

export const isNotNull = <T,>(value: T | null): value is T => value !== null;
